import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { PageMeta} from './seo-config.types';
import { SeoTagsInfo } from '../shared/util/seo-tags';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private title = inject(Title);
  private doc = inject(DOCUMENT);

  public setCanonical(href: string): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!link) {
      link = this.doc.createElement('link');
      link.rel = 'canonical';
      this.doc.head.appendChild(link);
    }
    link.href = href;
  }

  public applyPageSeo(p: SeoTagsInfo) {
    if(p.canonicalSlug) {
      this.setCanonical(location.origin + p.canonicalSlug);
    }

    this.title.setTitle(p.title);
    this.meta.addTags(p.tags, true);
    p.tags.forEach(tag => {
      if(tag.name && tag.content) {
        this.meta.updateTag({ name: tag.name, content: tag.content! });
      }
      if(tag.property && tag.content) {
        this.meta.updateTag({ property: tag.property, content: tag.content! });
      }
    })
  }

  public apply(pm: PageMeta) {
    this.title.setTitle(pm.title);

    // Concat and trim meta description to fit into 155-165 chars window
    const desc = this.makeMetaDescription(pm, 165);
    this.meta.updateTag({ name: 'description', content: desc });
    // this.meta.updateTag({ name: 'description', content: pm.description ?? '' });

    // canonical
    const href = location.origin + pm.urlFull;
    this.setCanonical(href);

    this.meta.updateTag({
      property: 'og:image',
      content: pm.images?.wide1200x675!,
    });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '675' });

    if (pm.images?.square1200) {
      this.meta.addTag({ property: 'og:image', content: pm.images.square1200 });
      this.meta.addTag({ property: 'og:image:width', content: '1200' });
      this.meta.addTag({ property: 'og:image:height', content: '1200' });
    }

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({
      name: 'twitter:image',
      content: pm.images?.wide1200x675!,
    });

    // Keywords
    const keywords = (pm.keywords ?? pm.feature.keywords)?.join(', ');
    if (keywords) this.meta.updateTag({ name: 'keywords', content: keywords });

    this.meta.updateTag({
      name: 'robots',
      content: 'index,follow,max-image-preview:large',
    });

    this.addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pm.title,
      primaryImageOfPage: pm.images?.wide1200x675
        ? {
            '@type': 'ImageObject',
            url: pm.images.wide1200x675,
            width: 1200,
            height: 675,
          }
        : undefined,
      image: [
        pm.images?.wide1200x675 && {
          '@type': 'ImageObject',
          url: pm.images.wide1200x675,
          width: 1200,
          height: 675,
        },
        pm.images?.classic1200x900 && {
          '@type': 'ImageObject',
          url: pm.images.classic1200x900,
          width: 1200,
          height: 900,
        },
        pm.images?.square1200 && {
          '@type': 'ImageObject',
          url: pm.images.square1200,
          width: 1200,
          height: 1200,
        },
      ].filter(Boolean),
    });
  }

  private makeMetaDescription(pm: PageMeta, maxLen = 165): string {
    const parts = [pm.description, pm.city?.description]
      .filter(Boolean)
      .map((s) => s!.trim());

    if (parts.length === 0) return '';

    let s = parts.join(' · ');

    if (s.length <= maxLen) return s;

    const cut = s.lastIndexOf(' ', maxLen - 1);
    return (cut > 0 ? s.slice(0, cut) : s.slice(0, maxLen - 1)).trimEnd() + '…';
  }

  private addJsonLd(obj: unknown) {
    const script = this.doc.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(obj);
    this.doc.head.appendChild(script);
  }
}
